import { useEffect, useRef, useState } from 'react';

/*
useReveal: lightweight scroll-into-view hook for entrance animations.

Returns a `ref` to attach to the element to observe and an `inView` boolean
that flips to true once the element enters the viewport. Pairs with the
CSS-module reveal classes (opacity/transform transitions) so no animation
library is needed.

@param {object} options
@param {number} options.threshold - IntersectionObserver threshold (default 0.15)
@param {string} options.rootMargin - IntersectionObserver rootMargin (default '0px 0px -10% 0px')
@param {boolean} options.once - stop observing after first reveal (default true)
@returns {[React.RefObject, boolean]} - [ref, inView]
*/
export default function useReveal({
  threshold = 0.15,
  rootMargin = '0px 0px -10% 0px',
  once = true,
} = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return undefined;
    }

    // Guard for SSR / unsupported browsers: reveal immediately.
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            if (once) {
              observer.unobserve(entry.target);
            }
          } else if (!once) {
            setInView(false);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return [ref, inView];
}
